import React, {ReactChild, ReactNode} from 'react'
import ContentEditable from 'react-contenteditable'
import stringify from 'stringify-entities'
import Tribute from 'tributejs'

import {OpenVariableForm} from '../../../providers/variables/use-variable-form'
import {IconButton} from '../buttons/icon-button'
import {
  DropdownBody,
  DropdownEmpty,
  DropdownEmptyMessage,
  DropdownEmptyTitle,
  DropdownFooter,
  DropdownItem,
  DropdownItemIcon
} from '../dropdown/content'
import {Dropdown} from '../dropdown/dropdown'
import {Icon, IconName} from '../icon/icon'
import {Tooltip} from '../tooltip/tooltip'
import {
  VariablePath,
  deserialiseVariables,
  displayedValue,
  getDataLabel,
  serialiseVariables
} from './helpers'
import {
  elementContainsSelection,
  getCurrentRange,
  placeCaretAtEnd,
  restoreRange
} from './selection-helpers'

import SVGAddVariable from '../../../assets/icons/add-variable-small.svg'
import {pasteAsText} from '../../../lib/helpers'
import {ButtonIcon} from '../../../views/action-flow/components/library/button'
import * as LegacyStyles from './legacy-styles'
import * as Styles from './styles'

let utilityDiv: HTMLDivElement

export const serialiseForEditor = (value: string, multiline = false) => {
  // serialise the raw value from the editor, with linebreaks preserved if
  // multiline, or stripped if not

  if (!utilityDiv) {
    // this div facilitates the conversion of div, br etc into \n
    // via innerText. It can't be display: none or it won't work
    utilityDiv = document.createElement('div')
    utilityDiv.style.position = 'absolute'
    utilityDiv.style.top = '-10000px'
    document.body.appendChild(utilityDiv)
  }

  utilityDiv.innerHTML = serialiseVariables(value)
  const plainTextValue = utilityDiv.innerText

  return multiline ? plainTextValue : plainTextValue.replace(/\n/g, '')
}

export const deserialiseForEditor = (value: string) =>
  deserialiseVariables(
    stringify(String(value || ''), {escapeOnly: true})
  ).replace(/(?:\r\n|\r|\n)/g, '<br>')

export interface TextEditorProps {
  value: string
  onChange: (value: string) => void
  variablePaths: VariablePath[]
  label: ReactNode
  upLabel?: ReactNode
  multiline?: boolean
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  openVariableForm?: OpenVariableForm
  addNewVariables?: boolean
  dropdownEmptyMessage?: string
  variableButtonDisabled?: boolean
  readOnly?: boolean
  legacyMode?: boolean
  icon?: IconName | ReactChild
}

export interface NumberEditorProps
  extends Omit<TextEditorProps, 'value' | 'onChange' | 'multiline'> {
  value?: number
  onChange: (value: number) => void
}

interface TextEditorState {
  localValue: string
  focused: boolean
  width?: number
}

const makeNewVariable = (variableName: string) => {
  const newVariable = document.createElement('span')
  newVariable.className = 'variable'
  newVariable.setAttribute('data-path', variableName)
  newVariable.setAttribute('contenteditable', 'false')
  newVariable.innerHTML = getDataLabel(variableName)

  return newVariable
}

export class TextEditorInner extends React.Component<
  TextEditorProps & {
    editorStyle?: React.CSSProperties
    labelStyle?: React.CSSProperties
  },
  TextEditorState
> {
  private ref: React.RefObject<HTMLDivElement>
  // private utilityRef: React.RefObject<HTMLDivElement>
  private widthRef: React.RefObject<HTMLDivElement>
  private tribute: Tribute<TributeOption> | undefined
  private lastRange: Range | undefined
  private tributeOnClick: undefined | ((e: MouseEvent) => void)

  constructor(props: TextEditorProps) {
    super(props)
    this.ref = React.createRef()
    // this.utilityRef = React.createRef()
    this.widthRef = React.createRef()
    this.state = {
      localValue: deserialiseForEditor(props.value),
      focused: false
    }
  }

  private static getTributeValues(
    variablePaths: VariablePath[],
    withNew?: boolean
  ) {
    const values = variablePaths.map(path => ({
      key: getDataLabel(path),
      value: getDataLabel(path),
      path
    }))

    return withNew
      ? values.concat({key: 'Add new variable', value: '__new', path: '__new'})
      : values
  }

  private insertNewVariable = () => {
    const el = this.ref.current

    if (el && this.props.openVariableForm) {
      this.props.openVariableForm('', (variableName: string) => {
        this.insertVariable(variableName)
      })
    }
  }

  private attachTribute() {
    const el = this.ref.current
    if (!el) {
      throw new Error('Tribute element missing')
    }
    if (this.tribute) {
      return
    }

    this.tribute = new Tribute({
      trigger: '+',
      requireLeadingSpace: false,
      selectTemplate: (item: TributeItem) => {
        if (item.original.path === '__new' && this.props.openVariableForm) {
          this.lastRange = getCurrentRange()
          this.props.openVariableForm('', (variableName: string) => {
            this.insertVariable(variableName)
          })
          return ''
        }
        return displayedValue(item.original.path)
      },
      values: TextEditorInner.getTributeValues(
        this.props.variablePaths,
        !!this.props.openVariableForm
      )
    })

    // need to trigger a manual update when variable inserted
    el.addEventListener('tribute-replaced', this.handleChange)

    this.tributeOnClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.matches('.variable')) {
        const name = target.getAttribute('data-path')
        if (name && this.props.openVariableForm) {
          this.props.openVariableForm(name)
        }
      }
    }

    el.addEventListener('click', this.tributeOnClick)

    this.tribute.attach(el)
  }

  private detachTribute() {
    const el = this.ref.current
    if (!el || !this.tribute) {
      return
    }

    el.removeEventListener('tribute-replaced', this.handleChange)
    if (this.tributeOnClick) {
      el.removeEventListener('click', this.tributeOnClick)
    }

    this.tribute.detach(el)
    this.tribute = undefined
    this.tributeOnClick = undefined
  }

  private setWidth = () => {
    if (this.widthRef.current) {
      const width = this.widthRef.current.offsetWidth
      if (width !== this.state.width) {
        this.setState({width})
      }
    }
  }

  public componentDidMount() {
    this.attachTribute()
    window.addEventListener('resize', this.setWidth)
    // hack fix disabled prop not working here
    // https://github.com/lovasoa/react-contenteditable/issues/214
    this.ref.current?.setAttribute(
      'contenteditable',
      this.props.readOnly ? 'false' : 'true'
    )
    this.setWidth()
  }

  public componentWillUnmount() {
    this.detachTribute()
    window.removeEventListener('resize', this.setWidth)
  }

  public componentDidUpdate(prevProps: TextEditorProps) {
    // tribute doesn't have an api for updating values (only appending) so
    // destroy it and rebuild if the values change
    // Note, the join is a safeguard in case the variablePaths array is unstable
    if (
      prevProps.variablePaths.join(' ') !== this.props.variablePaths.join(' ')
    ) {
      this.detachTribute()
      // tribute.detach uses a timeout for some reason, so it will warn if we
      // try to re-attach synchronously
      setTimeout(() => {
        this.attachTribute()
      })
    }
  }

  public static getDerivedStateFromProps(
    props: TextEditorProps,
    state: TextEditorState
  ) {
    // When not focused, act as a controlled component, but only update the
    // local rendered value if the incoming value has actually changed, to avoid
    // breaking selection ranges stored in this.lastRange.
    // Determine this by serialising the current local value and checking it
    // against the incoming value (which is serialised in handleChange)
    if (!state.focused) {
      const serialisedLocalValue = serialiseForEditor(
        state.localValue,
        props.multiline
      )

      if (serialisedLocalValue !== props.value) {
        return {
          localValue: deserialiseForEditor(props.value)
        }
      }
    }
    return null
  }

  private currentValue = () =>
    this.ref.current ? this.ref.current.innerHTML : ''

  private insertVariable = (variableName: string) => {
    const el = this.ref.current

    if (!el) {
      return
    }

    // restore selection if we can
    if (this.lastRange) {
      restoreRange(this.lastRange)
    }

    // if selection isn't within the right element, fall back to inserting at end
    if (!elementContainsSelection(el)) {
      placeCaretAtEnd(el)
    }

    const sel = window.getSelection()
    if (sel && sel.getRangeAt && sel.rangeCount) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(makeNewVariable(variableName))

      this.handleChange()
    } else {
      // this shouldn't happen, because of the insert-at-end fallback above
      console.warn('Could not insert new variable')
    }
  }

  private handleChange = () => {
    if (!this.props.readOnly) {
      const value = this.currentValue()
      this.setState({localValue: value})

      // pass the serialised text upstream whenever it changes
      const serialised = serialiseForEditor(value, this.props.multiline)
      this.props.onChange(serialised)
    }
  }

  private handleBlur = () => {
    this.setState({
      focused: false
    })
  }

  private handleFocus = () => {
    if (!this.props.readOnly) {
      this.setState({
        focused: true
      })
    }
  }

  public render() {
    const {localValue, focused, width} = this.state
    const {label, upLabel, onKeyPress, readOnly, legacyMode} = this.props

    const variables = this.props.variablePaths.map(path => ({
      key: getDataLabel(path),
      value: getDataLabel(path),
      path
    }))

    const addNewVariables =
      this.props.addNewVariables !== undefined
        ? this.props.addNewVariables
        : true

    const dropdownEmptyMessage =
      this.props.dropdownEmptyMessage !== undefined
        ? this.props.dropdownEmptyMessage
        : addNewVariables
          ? 'Variables enable you to populate cards with dynamic data. Insert one by typing the + key, or by adding manually here.'
          : 'Variables enable you to populate cards with dynamic data. To add one, go to the variables tab.'

    const Wrapper = legacyMode ? LegacyStyles.Wrapper : Styles.Wrapper
    const Label = legacyMode ? LegacyStyles.Label : Styles.Label
    const StyledTextEditor = legacyMode
      ? LegacyStyles.StyledTextEditor
      : Styles.StyledTextEditor
    const TributeStyle = legacyMode
      ? LegacyStyles.TributeStyle
      : Styles.TributeStyle
    const AddVariableWrapper = legacyMode
      ? LegacyStyles.AddVariableWrapper
      : Styles.AddVariableWrapper

    return (
      <Wrapper className={localValue || focused ? 'labelUp' : ''}>
        <Label style={this.props.labelStyle}>
          {localValue || focused || readOnly
            ? upLabel
              ? upLabel
              : label
            : label}
        </Label>
        <StyledTextEditor readOnly={readOnly}>
          <ContentEditable
            innerRef={this.ref}
            html={localValue}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onKeyPress={onKeyPress}
            style={{
              // hide element until the initial width measurement has taken
              // place - this ensures we get an accurate measurement, not
              // influenced by the content of this div, which might spill out
              // horizontally if it doesn't have an explicit width set
              ...(width === undefined ? {display: 'none'} : {}),
              width: width + 'px',
              ...this.props.editorStyle
            }}
            className="editable"
            disabled={readOnly} // this should set contenteditable to false, its not see hack fix for this in componentDidMount
            onPaste={e => {
              pasteAsText(e)
              this.handleChange()
            }}
          />
          {!readOnly && <TributeStyle />}
          <div
            // allows us to measure available width, and set it for the editor
            ref={this.widthRef}
          />
        </StyledTextEditor>
        {!readOnly && (
          <AddVariableWrapper>
            <Dropdown
              style={{zIndex: 15}}
              minWidth={300}
              trigger={(handleClick, ref, isOpen) => (
                <Tooltip
                  content="Insert variable"
                  position="left"
                  isLight={false}
                  disabled={isOpen}
                >
                  {legacyMode ? (
                    <IconButton
                      onClick={() => {
                        this.lastRange = getCurrentRange()
                        handleClick()
                      }}
                      ref={ref}
                      icon="variable_add"
                      size="large"
                      variant="color"
                      disabled={this.props.variableButtonDisabled}
                    />
                  ) : (
                    <ButtonIcon
                      appearance="transparent"
                      onClick={e => {
                        e.preventDefault()
                        this.lastRange = getCurrentRange()
                        handleClick()
                      }}
                      ref={ref as React.Ref<HTMLButtonElement>}
                      disabled={this.props.variableButtonDisabled}
                    >
                      <SVGAddVariable />
                    </ButtonIcon>
                  )}
                </Tooltip>
              )}
            >
              {closeDropdown => (
                <>
                  {variables.length > 0 ? (
                    <DropdownBody>
                      {variables.map((variable, index) => (
                        <DropdownItem key={index}>
                          <a
                            onClick={() => {
                              closeDropdown()
                              this.insertVariable(variable.path)
                            }}
                          >
                            {variable.key}
                          </a>
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  ) : (
                    <DropdownBody
                      onClick={() => {
                        closeDropdown()
                      }}
                    >
                      <DropdownEmpty style={{maxWidth: '400px'}}>
                        <Icon name="plug" />
                        <DropdownEmptyTitle>
                          No variables yet
                        </DropdownEmptyTitle>
                        <DropdownEmptyMessage>
                          {dropdownEmptyMessage}
                        </DropdownEmptyMessage>
                      </DropdownEmpty>
                    </DropdownBody>
                  )}
                  {addNewVariables && (
                    <DropdownFooter>
                      <DropdownItem isAction={true}>
                        <a
                          onClick={() => {
                            closeDropdown()
                            this.insertNewVariable()
                          }}
                        >
                          <DropdownItemIcon icon="add" variant="color" />
                          New variable
                        </a>
                      </DropdownItem>
                    </DropdownFooter>
                  )}
                </>
              )}
            </Dropdown>
          </AddVariableWrapper>
        )}
      </Wrapper>
    )
  }
}

interface TributeOption {
  key: string
  value: string
  path: VariablePath
}

interface TributeItem {
  index: number
  original: TributeOption
  score: number
  string: string
}

export const TextEditor = (
  props: TextEditorProps & {
    editorStyle?: React.CSSProperties
    labelStyle?: React.CSSProperties
  }
) => {
  const legacyMode = props.legacyMode
  const TextEditorWrapper = legacyMode
    ? LegacyStyles.TextEditorWrapper
    : Styles.TextEditorWrapper
  const IconWrap = legacyMode ? LegacyStyles.IconWrap : Styles.IconWrap
  const EditorWrap = legacyMode ? LegacyStyles.EditorWrap : Styles.EditorWrap
  return (
    <TextEditorWrapper readOnly={props.readOnly}>
      {props.icon ? (
        <IconWrap>
          {legacyMode && typeof props.icon === 'string' ? (
            <Icon name={props.icon as IconName} />
          ) : (
            props.icon
          )}
        </IconWrap>
      ) : null}
      <EditorWrap>
        <TextEditorInner {...props} />
      </EditorWrap>
    </TextEditorWrapper>
  )
}

const numberRegex = /^\d*\.?\d*$/
const numbersOnly = (e: React.KeyboardEvent<HTMLDivElement>) => {
  const charStr = String.fromCharCode(e.charCode)
  // @ts-ignore - ContentEditable props type is wrong
  const attemptedValue = `${e.target.textContent}${charStr}`

  if (!numberRegex.test(attemptedValue)) {
    e.preventDefault()
  }
}

export const NumberEditorInner = (props: Omit<NumberEditorProps, 'spill'>) => {
  const {value, onChange, ...rest} = props
  return (
    <TextEditorInner
      {...rest}
      value={value ? String(value) : ''}
      onKeyPress={numbersOnly}
      onChange={s => {
        const n = Number(s.replace(/[^0-9.]/g, ''))
        return onChange(isNaN(n) ? 0 : n)
      }}
    />
  )
}

export const NumberEditor = (props: NumberEditorProps) => {
  const legacyMode = props.legacyMode
  const TextEditorWrapper = legacyMode
    ? LegacyStyles.TextEditorWrapper
    : Styles.TextEditorWrapper
  const IconWrap = legacyMode ? LegacyStyles.IconWrap : Styles.IconWrap
  const EditorWrap = legacyMode ? LegacyStyles.EditorWrap : Styles.EditorWrap
  return (
    <TextEditorWrapper readOnly={props.readOnly}>
      {props.icon ? (
        <IconWrap>
          {legacyMode && typeof props.icon === 'string' ? (
            <Icon name={props.icon as IconName} />
          ) : (
            props.icon
          )}
        </IconWrap>
      ) : null}
      <EditorWrap>
        <NumberEditorInner {...props} />
      </EditorWrap>
    </TextEditorWrapper>
  )
}
